import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Box, Grid, Card, CardContent, Typography, TextField, Button, Chip, Tooltip, Select, MenuItem, FormControlLabel, Switch } from '@mui/material';
import {
  LineChart as RLineChart,
  Line as RLine,
  BarChart as RBarChart,
  Bar as RBar,
  CartesianGrid as RGrid,
  XAxis as RXAxis,
  YAxis as RYAxis,
  Tooltip as RTooltip,
  ResponsiveContainer as RResponsiveContainer,
  Legend as RLegend
} from 'recharts';
import { getTrialGatingMetrics, buildDateRange } from '../utils/analytics-api';
import { adaptTrialGating } from '../utils/trialGatingAdapter';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import styles from './Analytics.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkAuthenticatedAdmin } from '../utils/client-functions';
import { useErrorSetter } from '../hooks/useUiMessages';

const AntiAbuseLinkages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setError = useErrorSetter();
  const token = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');

  const [deviceId, setDeviceId] = useState('');
  const [ipHash, setIpHash] = useState('');
  const [uaHash, setUaHash] = useState('');
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [fingerprints, setFingerprints] = useState([]);
  const [users, setUsers] = useState([]);
  // Trial gating metrics state
  const [gatingLoading, setGatingLoading] = useState(false);
  const [gatingData, setGatingData] = useState({ kpis:{}, reasons:[], daily:[], dailyDeviceIp:[] });
  const [startDate, setStartDate] = useState(''); // yyyy-MM-dd
  const [endDate, setEndDate] = useState('');   // yyyy-MM-dd
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [autoMinutes, setAutoMinutes] = useState(2);

  // White-themed input styling for date/time pickers
  const whitePickerSx = {
    '& .MuiInputBase-input': { color: '#fff' },
    '& .MuiInputLabel-root': { color: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
    '& .MuiSvgIcon-root': { color: '#fff' },
    '& input[type="date"]::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
    '& input[type="time"]::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
  };

  // White-themed styling for Select inputs and their dropdown menus
  const whiteSelectSx = {
    '& .MuiInputBase-input': { color: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
    '& .MuiSvgIcon-root': { color: '#fff' },
  };

  const whiteMenuProps = {
    PaperProps: {
      sx: {
        bgcolor: '#1f1f1f',
        color: '#fff',
        '& .MuiMenuItem-root': { color: '#fff' },
        '& .Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' },
      },
    },
  };

  // Hydrate from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const d = params.get('deviceId') || '';
    const i = params.get('ipHash') || '';
    const u = params.get('uaHash') || '';
    const l = parseInt(params.get('limit') || '100', 10);
    setDeviceId(d);
    setIpHash(i);
    setUaHash(u);
    setLimit(l);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL when inputs change (only when a search has been performed)
  const syncUrl = () => {
    const params = new URLSearchParams();
    if (deviceId) params.set('deviceId', deviceId);
    if (ipHash) params.set('ipHash', ipHash);
    if (uaHash) params.set('uaHash', uaHash);
    if (limit) params.set('limit', String(limit));
    const newUrl = `${location.pathname}?${params.toString()}`;
    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, '', newUrl);
    }
  };

  const fetchLinkages = async () => {
    try {
      await checkAuthenticatedAdmin();
    } catch {
      return navigate('/');
    }
    if (!deviceId && !ipHash && !uaHash) {
      return setError('Enter deviceId or ipHash and/or uaHash');
    }
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (deviceId) params.set('deviceId', deviceId);
      if (ipHash) params.set('ipHash', ipHash);
      if (uaHash) params.set('uaHash', uaHash);
      if (limit) params.set('limit', String(limit));
      const resp = await axios.get(`/origin/anti-abuse/linkages?${params.toString()}`, { headers: { 'x-access-token': token } });
      setFingerprints(resp?.data?.fingerprints || []);
      setUsers(resp?.data?.users || []);
      syncUrl();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load linkages');
    } finally {
      setLoading(false);
    }
  };

  // Auto-search on mount when query params present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('deviceId') || params.get('ipHash') || params.get('uaHash')) {
      fetchLinkages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Convert date inputs to ISO range (start of day/end of day)
  const toIsoRange = () => {
    if (!startDate || !endDate) {
      const range = buildDateRange(30);
      return { startDate: range.startDate, endDate: range.endDate };
    }
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59.999`);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  };

  // Fetch trial gating metrics using current state range
  const fetchGating = async () => {
    try {
      await checkAuthenticatedAdmin();
    } catch {
      return navigate('/');
    }
    try {
      setGatingLoading(true);
      const { startDate: s, endDate: e } = toIsoRange();
      const resp = await getTrialGatingMetrics({ startDate: s, endDate: e });
  setGatingData(adaptTrialGating(resp));
    } catch (e) {
      setError(e?.message || 'Failed to load trial gating metrics');
    } finally {
      setGatingLoading(false);
    }
  };

  useEffect(() => {
    fetchGating();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh interval (1–5 minutes)
  useEffect(() => {
    if (!autoRefresh) return;
    const mins = Math.min(Math.max(parseInt(autoMinutes || 1, 10), 1), 5);
    const id = setInterval(() => {
      if (!gatingLoading) fetchGating();
    }, mins * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, autoMinutes]);

  const usersSummary = useMemo(() => {
    const total = users.length;
    const trialEligible = users.filter(u => u.eligibility?.freeTrialEligible).length;
    const suspects = users.filter(u => u.flags?.repeatSignupSuspect).length;
    const activeSubs = users.filter(u => u.subscriptions?.resume?.subscribed).length;
    return { total, trialEligible, suspects, activeSubs };
  }, [users]);

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
    } catch {}
  };

  return (
    <div className={styles.analyticsContainer}>
      <AdminSideMenu />
      <div className={styles.analyticsContent}>
        <div className={styles.analyticsHeader}>
          <div className={styles.headerLeft}>
            <Typography variant="h4" className={styles.pageTitle}>Anti‑Abuse Linkages</Typography>
            <Typography variant="body2" className={styles.pageSubtitle}>Inspect fingerprints and linked users used for trial gating</Typography>
          </div>
        </div>

        <Box sx={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField size="small" label="Device ID" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} sx={{ minWidth: 260 }} />
          <TextField size="small" label="IP Hash" value={ipHash} onChange={(e) => setIpHash(e.target.value)} sx={{ minWidth: 240 }} />
          <TextField size="small" label="UA Hash" value={uaHash} onChange={(e) => setUaHash(e.target.value)} sx={{ minWidth: 240 }} />
          <Select
            size="small"
            value={String(limit)}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            sx={whiteSelectSx}
            MenuProps={whiteMenuProps}
          >
            {[50, 100, 200, 500].map(n => <MenuItem key={n} value={String(n)}>{n} limit</MenuItem>)}
          </Select>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => { setDeviceId(''); setIpHash(''); setUaHash(''); setUsers([]); setFingerprints([]); syncUrl(); }}>Reset</Button>
            <Button variant="contained" onClick={fetchLinkages} disabled={loading}>{loading ? 'Loading…' : 'Search'}</Button>
          </Box>
        </Box>

        {/* KPIs */}
        <Grid container spacing={2} className={styles.kpiContainer}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Fingerprints</Typography>
                <Typography className={styles.kpiValue}>{fingerprints.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Linked Users</Typography>
                <Typography className={styles.kpiValue}>{usersSummary.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Trial‑Eligible</Typography>
                <Typography className={styles.kpiValue}>{usersSummary.trialEligible}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Suspects</Typography>
                <Typography className={styles.kpiValue}>{usersSummary.suspects}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Fingerprints table */}
        <Box className={styles.chartContainer} sx={{ p: 0, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Fingerprints</Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate' }}>
              <thead>
                <tr>
                  {['deviceId','ipHash','uaHash','users','signupAttempts','firstSeen','lastSeen','lastSignupAt'].map(h => (
                    <th key={h} style={{ textAlign: 'left', background: '#c0d1d457', padding: '12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fingerprints.map((f, idx) => (
                  <tr key={f._id || idx}>
                    <td style={{ padding: '10px' }}>
                      <Tooltip title="Click to copy"><span style={{ cursor: 'pointer' }} onClick={() => copy(f.deviceId)}>{f.deviceId || '—'}</span></Tooltip>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <Tooltip title="Click to copy"><span style={{ cursor: 'pointer' }} onClick={() => copy(f.ipHash)}>{f.ipHash || '—'}</span></Tooltip>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <Tooltip title="Click to copy"><span style={{ cursor: 'pointer' }} onClick={() => copy(f.uaHash)}>{f.uaHash || '—'}</span></Tooltip>
                    </td>
                    <td style={{ padding: '10px' }}>{(f.users || []).length}</td>
                    <td style={{ padding: '10px' }}>{f.signupAttempts ?? 0}</td>
                    <td style={{ padding: '10px' }}>{f.firstSeen ? new Date(f.firstSeen).toLocaleString() : '—'}</td>
                    <td style={{ padding: '10px' }}>{f.lastSeen ? new Date(f.lastSeen).toLocaleString() : '—'}</td>
                    <td style={{ padding: '10px' }}>{f.lastSignupAt ? new Date(f.lastSignupAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Users table */}
        <Box className={styles.chartContainer} sx={{ p: 0 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Linked Users</Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate' }}>
              <thead>
                <tr>
                  {['email','name','createdAt','trialEligible','score','reasons','suspect','subscription'].map(h => (
                    <th key={h} style={{ textAlign: 'left', background: '#c0d1d457', padding: '12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => {
                  const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
                  const elig = u.eligibility || {};
                  const flags = u.flags || {};
                  const sub = u.subscriptions?.resume || {};
                  return (
                    <tr key={u._id || idx}>
                      <td style={{ padding: '10px' }}>{u.email}</td>
                      <td style={{ padding: '10px' }}>{name}</td>
                      <td style={{ padding: '10px' }}>{u.createdAt ? new Date(u.createdAt).toLocaleString() : '—'}</td>
                      <td style={{ padding: '10px' }}>
                        {elig.freeTrialEligible ? <Chip size="small" color="success" label="yes"/> : <Chip size="small" color="default" label="no"/>}
                      </td>
                      <td style={{ padding: '10px' }}>{Number(elig.score || 0)}</td>
                      <td style={{ padding: '10px' }}>
                        {(elig.reasons || []).length === 0 ? '—' : (
                          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                            {(elig.reasons || []).slice(0,6).map((r,i) => (<Chip key={i} size="small" label={r} />))}
                            {(elig.reasons || []).length > 6 && <Chip size="small" variant="outlined" label={`+${(elig.reasons||[]).length-6} more`} />}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {flags.repeatSignupSuspect ? <Chip size="small" color="warning" label="suspect"/> : '—'}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {sub.subscribed ? `${sub.duration || 'active'}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Trial Gating Metrics */}
        <Box sx={{ mt: 4 }} />
        <Typography variant="h5" className={styles.pageTitle} sx={{ mb: 1 }}>Trial Gating Metrics</Typography>
        <Typography variant="body2" className={styles.pageSubtitle} sx={{ mb: 2 }}>Last 30 days. These reflect outcomes from the signup trial gate.</Typography>
        <Box sx={{ display: 'flex', gap: 12, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={whitePickerSx}
            />
            <TextField
              size="small"
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={whitePickerSx}
            />
            <Button variant="contained" onClick={fetchGating} disabled={gatingLoading}>{gatingLoading ? 'Loading…' : 'Apply'}</Button>
            <Button variant="outlined" onClick={() => { setStartDate(''); setEndDate(''); fetchGating(); }} disabled={gatingLoading}>Reset</Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />} label="Auto‑refresh" />
            <Select
              size="small"
              value={String(autoMinutes)}
              onChange={(e) => setAutoMinutes(parseInt(e.target.value, 10))}
              disabled={!autoRefresh}
              sx={whiteSelectSx}
              MenuProps={whiteMenuProps}
            >
              {[1,2,5].map(m => <MenuItem key={m} value={String(m)}>{m} min</MenuItem>)}
            </Select>
            <Button variant="outlined" onClick={fetchGating} disabled={gatingLoading}>{gatingLoading ? 'Refreshing…' : 'Refresh Now'}</Button>
          </Box>
        </Box>

        {/* KPIs */}
        <Grid container spacing={2} className={styles.kpiContainer} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Denied (Total)</Typography>
                <Typography className={styles.kpiValue}>{gatingData.kpis?.totalDenied ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Denied (Device/IP)</Typography>
                <Typography className={styles.kpiValue}>{gatingData.kpis?.deviceIpDenied ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Denial Rate</Typography>
                <Typography className={styles.kpiValue}>{(gatingData.kpis?.denialRatePct ?? 0).toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Granted</Typography>
                <Typography className={styles.kpiValue}>{gatingData.kpis?.totalGranted ?? 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Grid */}
        <Grid container spacing={3} className={styles.chartsGrid}>
          {/* Daily Denied vs Granted */}
          <Grid item xs={12} md={6}>
            <Card className={styles.chartCard}>
              <CardContent>
                <Typography variant="h6" className={styles.chartTitle}>Daily: Denied vs Granted</Typography>
                <div style={{ width: '100%', height: 280 }}>
                  <RResponsiveContainer width="100%" height="100%">
                    <RLineChart data={gatingData.daily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <RGrid strokeDasharray="3 3" />
                      <RXAxis dataKey="date" />
                      <RYAxis allowDecimals={false} />
                      <RTooltip />
                      <RLegend />
                      <RLine type="monotone" dataKey="denied" stroke="#db3236" strokeWidth={2} name="Denied" dot={false} />
                      <RLine type="monotone" dataKey="granted" stroke="#3cba54" strokeWidth={2} name="Granted" dot={false} />
                    </RLineChart>
                  </RResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* Daily Device/IP Denied */}
          <Grid item xs={12} md={6}>
            <Card className={styles.chartCard}>
              <CardContent>
                <Typography variant="h6" className={styles.chartTitle}>Daily: Device/IP Denied</Typography>
                <div style={{ width: '100%', height: 280 }}>
                  <RResponsiveContainer width="100%" height="100%">
                    <RBarChart data={gatingData.dailyDeviceIp} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <RGrid strokeDasharray="3 3" />
                      <RXAxis dataKey="date" />
                      <RYAxis allowDecimals={false} />
                      <RTooltip />
                      <RLegend />
                      <RBar dataKey="deviceIpDenied" fill="#6f9da0" name="Device/IP Denied" />
                    </RBarChart>
                  </RResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </Grid>

          {/* Top Reasons (Denials) */}
          <Grid item xs={12}>
            <Card className={styles.chartCard}>
              <CardContent>
                <Typography variant="h6" className={styles.chartTitle}>Top Denial Reasons</Typography>
                <div style={{ width: '100%', height: 320 }}>
                  <RResponsiveContainer width="100%" height="100%">
                    <RBarChart data={gatingData.reasons} layout="horizontal" margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <RXAxis dataKey="label" angle={-20} textAnchor="end" interval={0} height={60} />
                      <RYAxis allowDecimals={false} />
                      <RTooltip />
                      <RLegend />
                      <RBar dataKey="value" fill="#3E8F93" name="Count" />
                    </RBarChart>
                  </RResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default AntiAbuseLinkages;
